import type { FC, ReactNode } from 'react'

import { useEffect, useRef, useState } from 'react'
import { useRouter } from 'next/router'

import {
    AnimatePresence,
    motion,
    useMotionValueEvent,
    useScroll
} from 'motion/react'

import { cn } from '@/lib/utils'
import { buttonStyles } from '@/lib/button-styles'
import SmartLink from '@/components/generals/smart-link'
import SoftlyLogo from '@/components/generals/softly-logo'
import { NAV_LINKS, siteConfig } from '@/constants/site'

const navLinkClass = cn(
    'relative rounded-sm text-[14px] text-cocoa',
    'after:absolute after:-bottom-1 after:left-0 after:h-px after:w-full after:origin-left after:scale-x-0 after:bg-cocoa after:transition-transform after:duration-500 hover:after:scale-x-100',
    'focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-cocoa'
)

const SiteHeader: FC = (): ReactNode => {
    const router = useRouter()
    const { scrollY } = useScroll()
    const lastY = useRef(0)
    const [scrolled, setScrolled] = useState(false)
    const [hidden, setHidden] = useState(false)
    const [open, setOpen] = useState(false)

    // Tuck the bar away while reading down the page, bring it back on the way up.
    useMotionValueEvent(scrollY, 'change', latest => {
        const previous = lastY.current

        lastY.current = latest
        setScrolled(latest > 24)

        if (open || latest < 320) {
            setHidden(false)
            return
        }

        if (latest > previous + 4) setHidden(true)
        else if (latest < previous - 4) setHidden(false)
    })

    useEffect(() => {
        if (!open) return

        const onKeyDown = (event: KeyboardEvent): void => {
            if (event.key === 'Escape') setOpen(false)
        }

        window.addEventListener('keydown', onKeyDown)

        return () => window.removeEventListener('keydown', onKeyDown)
    }, [open])

    useEffect(() => {
        const close = (): void => setOpen(false)

        router.events.on('routeChangeStart', close)

        return () => router.events.off('routeChangeStart', close)
    }, [router.events])

    const solid = scrolled || open

    return (
        <header
            className={cn(
                'fixed inset-x-0 top-0 z-50 transition-transform duration-700 ease-[cubic-bezier(0.22,1,0.36,1)]',
                hidden && '-translate-y-full'
            )}
        >
            <div
                className={cn(
                    'grid transition-[grid-template-rows,opacity] duration-700 ease-out',
                    solid ? 'grid-rows-[0fr] opacity-0' : 'grid-rows-[1fr] opacity-100'
                )}
                aria-hidden={solid}
            >
                <div className='min-h-0 overflow-hidden'>
                    <div className='mx-auto flex h-8 max-w-[1440px] items-center justify-between px-5 text-[12px] tracking-[0.04em] text-cocoa lg:px-10'>
                        <SmartLink href='/' tabIndex={solid ? -1 : undefined} className='rounded-sm underline-offset-4 hover:underline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cocoa'>
                            {siteConfig.domain}
                        </SmartLink>

                        <a
                            href={`mailto:${siteConfig.email}`}
                            tabIndex={solid ? -1 : undefined}
                            className='rounded-sm underline-offset-4 hover:underline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cocoa'
                        >
                            {siteConfig.email}
                        </a>
                    </div>

                    <div className='mx-5 h-px bg-cocoa/10 lg:mx-10' />
                </div>
            </div>

            <div
                className={cn(
                    'transition-[background-color,box-shadow] duration-700 ease-out',
                    solid ? 'bg-cream/85 shadow-[0_1px_0_rgb(74_43_32/0.08)] backdrop-blur-xl' : 'bg-transparent'
                )}
            >
                <nav aria-label='Main' className='mx-auto grid h-16 max-w-[1440px] grid-cols-[1fr_auto_1fr] items-center px-5 lg:px-10'>
                    <SmartLink
                        href='/'
                        aria-label='Softly, back to the top'
                        className='justify-self-start rounded-sm focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-cocoa'
                    >
                        <SoftlyLogo />
                    </SmartLink>

                    <ul className='hidden items-center gap-9 md:flex'>
                        {NAV_LINKS.map(link => {
                            const current = router.pathname === link.href

                            return (
                                <li key={link.href}>
                                    <SmartLink
                                        href={link.href}
                                        aria-current={current ? 'page' : undefined}
                                        className={cn(navLinkClass, current && 'font-medium after:scale-x-100')}
                                    >
                                        {link.label}
                                    </SmartLink>
                                </li>
                            )
                        })}
                    </ul>

                    <div className='flex items-center justify-end gap-2'>
                        <SmartLink href='/#join' className={buttonStyles('primary', 'sm')}>
                            Get Started
                        </SmartLink>

                        <button
                            type='button'
                            className='grid size-9 place-items-center rounded-[4px] border border-cocoa/25 text-cocoa transition-colors duration-500 hover:border-cocoa md:hidden focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cocoa'
                            aria-expanded={open}
                            aria-controls='mobile-menu'
                            aria-label={open ? 'Close menu' : 'Open menu'}
                            onClick={() => setOpen(value => !value)}
                        >
                            <span className='relative block h-2.5 w-4' aria-hidden='true'>
                                <span className={cn('absolute left-0 top-0 h-px w-4 bg-current transition-transform duration-500', open && 'translate-y-[5px] rotate-45')} />
                                <span className={cn('absolute bottom-0 left-0 h-px w-4 bg-current transition-transform duration-500', open && '-translate-y-[4px] -rotate-45')} />
                            </span>
                        </button>
                    </div>
                </nav>

                <AnimatePresence initial={false}>
                    {open && (
                        <motion.div
                            id='mobile-menu'
                            key='mobile-menu'
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: 'auto', opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                            className='overflow-hidden md:hidden'
                        >
                            <ul className='mx-5 border-t border-cocoa/10 pb-6 pt-2'>
                                {NAV_LINKS.map(link => (
                                    <li key={link.href} className='border-b border-cocoa/10'>
                                        <SmartLink
                                            href={link.href}
                                            onNavigate={() => setOpen(false)}
                                            aria-current={router.pathname === link.href ? 'page' : undefined}
                                            className='flex items-center justify-between py-4 text-[17px] text-cocoa focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cocoa'
                                        >
                                            {link.label}
                                            <span className={cn('size-1.5 rounded-full', router.pathname === link.href ? 'bg-cocoa' : 'bg-cocoa/30')} aria-hidden='true' />
                                        </SmartLink>
                                    </li>
                                ))}
                            </ul>
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>
        </header>
    )
}

export default SiteHeader
